
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { authConfig } from "./authConfig";

interface ForgotPasswordFormProps {
  onCancel: () => void;
  onLoading: (isLoading: boolean) => void;
}

export const ForgotPasswordForm = ({ onCancel, onLoading }: ForgotPasswordFormProps) => {
  const [resetEmail, setResetEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter your email address",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      onLoading(true);

      // Sign out any existing session before requesting password reset
      await supabase.auth.signOut();

      const resetPasswordOptions = {
        redirectTo: authConfig.getRedirectUrl()
      };

      const { error } = await supabase.auth.resetPasswordForEmail(
        resetEmail, 
        resetPasswordOptions
      );
      
      if (error) throw error;
      
      toast({
        title: "Password Reset Email Sent",
        description: "Check your email for the password reset link. The link will expire in 24 hours.",
      });
      onCancel();
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to send reset email. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
      onLoading(false);
    }
  };

  return (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <div>
        <Label htmlFor="reset-email">Email address</Label>
        <Input
          id="reset-email"
          type="email"
          value={resetEmail}
          onChange={(e) => setResetEmail(e.target.value)}
          placeholder="Enter your email"
          required
          className="mt-1"
          disabled={isSubmitting}
        />
      </div>
      <div className="flex gap-3">
        <Button 
          type="submit" 
          className="flex-1"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <span className="animate-spin mr-2">⏳</span>
              Sending...
            </span>
          ) : (
            "Send Reset Link"
          )}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          className="flex-1"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};
